import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Package, ArrowUpCircle, ArrowDownCircle, RefreshCw,
  TrendingUp, TrendingDown, AlertTriangle, Loader2, Search,
  Plus, Minus, History, Filter
} from 'lucide-react';

const StockManagementPanel = ({ stationId }) => {
  const { api } = useAuth();
  const [stockItems, setStockItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [updateQuantity, setUpdateQuantity] = useState('');
  const [updateType, setUpdateType] = useState('entrada');
  const [updateNotes, setUpdateNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showMovements, setShowMovements] = useState(false);

  const loadData = useCallback(async () => {
    if (!stationId) return;
    
    setLoading(true);
    try {
      const [stockRes, summaryRes, movementsRes] = await Promise.all([
        api().get(`/stock/station/${stationId}`),
        api().get(`/stock/summary/${stationId}`),
        api().get(`/stock/movements/${stationId}?limit=50`)
      ]);
      setStockItems(stockRes.data);
      setSummary(summaryRes.data);
      setMovements(movementsRes.data);
    } catch (err) {
      console.error('Error loading stock data:', err);
    }
    setLoading(false);
  }, [api, stationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateStock = async () => {
    if (!selectedProduct || !updateQuantity) return;
    
    setUpdating(true);
    try {
      const currentQty = selectedProduct.quantity;
      let newQty;
      
      if (updateType === 'entrada') {
        newQty = currentQty + parseInt(updateQuantity);
      } else if (updateType === 'saida_manual') {
        newQty = Math.max(0, currentQty - parseInt(updateQuantity));
      } else {
        newQty = parseInt(updateQuantity);
      }
      
      await api().post(`/stock/update/${stationId}/${selectedProduct.product_id}`, {
        quantity: newQty,
        movement_type: updateType,
        notes: updateNotes || undefined
      });
      
      toast.success('Stock actualizado com sucesso!');
      setSelectedProduct(null);
      setUpdateQuantity('');
      setUpdateNotes('');
      loadData();
    } catch (err) {
      toast.error('Erro ao actualizar stock');
    }
    setUpdating(false);
  };

  const filteredItems = stockItems.filter(item =>
    item.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getMovementIcon = (type) => {
    switch (type) {
      case 'entrada':
        return <ArrowUpCircle className="w-4 h-4 text-green-400" />;
      case 'saida_venda':
      case 'saida_manual':
        return <ArrowDownCircle className="w-4 h-4 text-red-400" />;
      case 'devolucao':
        return <RefreshCw className="w-4 h-4 text-blue-400" />;
      default:
        return <Package className="w-4 h-4 text-zinc-400" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-AO', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-sonangol-orange" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Produtos</p>
                <p className="text-white font-bold text-xl">{summary.total_products}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Total Unidades</p>
                <p className="text-white font-bold text-xl">{summary.total_units}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Stock Baixo</p>
                <p className="text-white font-bold text-xl">{summary.low_stock_count}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs">Esgotados</p>
                <p className="text-white font-bold text-xl">{summary.out_of_stock_count}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Movements Stats */}
      {summary?.movements_30_days && (
        <div className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600">
          <h3 className="font-barlow font-bold text-white mb-3">Movimentos (últimos 30 dias)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <ArrowUpCircle className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-green-400 font-bold">+{summary.movements_30_days.units_entered}</p>
                <p className="text-xs text-zinc-400">{summary.movements_30_days.total_entries} entradas</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ArrowDownCircle className="w-6 h-6 text-red-400" />
              <div>
                <p className="text-red-400 font-bold">-{summary.movements_30_days.units_exited}</p>
                <p className="text-xs text-zinc-400">{summary.movements_30_days.total_exits} saídas</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toggle between Stock List and Movements */}
      <div className="flex gap-2">
        <Button
          variant={!showMovements ? "default" : "outline"}
          onClick={() => setShowMovements(false)}
          className={!showMovements ? 'bg-sonangol-orange' : 'border-zinc-600 text-zinc-300'}
        >
          <Package className="w-4 h-4 mr-2" />
          Stock Actual
        </Button>
        <Button
          variant={showMovements ? "default" : "outline"}
          onClick={() => setShowMovements(true)}
          className={showMovements ? 'bg-sonangol-orange' : 'border-zinc-600 text-zinc-300'}
        >
          <History className="w-4 h-4 mr-2" />
          Histórico
        </Button>
      </div>

      {!showMovements ? (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar produto..."
              className="pl-10 bg-zinc-700 border-zinc-600 text-white"
            />
          </div>

          {/* Stock List */}
          <div className="space-y-2">
            {filteredItems.map((item) => (
              <div
                key={item.product_id}
                className={`bg-zinc-700/50 rounded-xl p-4 border transition-all cursor-pointer hover:border-sonangol-orange ${
                  selectedProduct?.product_id === item.product_id 
                    ? 'border-sonangol-orange' 
                    : 'border-zinc-600'
                }`}
                onClick={() => setSelectedProduct(selectedProduct?.product_id === item.product_id ? null : item)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{item.product_name}</p>
                    <p className="text-zinc-400 text-xs">{item.product_category}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold text-lg ${
                      item.is_out_of_stock 
                        ? 'text-red-400' 
                        : item.is_low_stock 
                          ? 'text-yellow-400' 
                          : 'text-green-400'
                    }`}>
                      {item.quantity} un.
                    </p>
                    <p className="text-xs text-zinc-500">Mín: {item.min_threshold}</p>
                  </div>
                </div>

                {/* Update Form */}
                {selectedProduct?.product_id === item.product_id && (
                  <div className="mt-4 pt-4 border-t border-zinc-600 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      {['entrada', 'saida_manual', 'ajuste'].map((type) => (
                        <button
                          key={type}
                          onClick={() => setUpdateType(type)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            updateType === type
                              ? type === 'entrada' 
                                ? 'bg-green-500 text-white'
                                : type === 'saida_manual'
                                  ? 'bg-red-500 text-white'
                                  : 'bg-blue-500 text-white'
                              : 'bg-zinc-600 text-zinc-300'
                          }`}
                        >
                          {type === 'entrada' && <Plus className="w-3 h-3 inline mr-1" />}
                          {type === 'saida_manual' && <Minus className="w-3 h-3 inline mr-1" />}
                          {type === 'entrada' ? 'Entrada' : type === 'saida_manual' ? 'Saída' : 'Ajuste'}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="0"
                        value={updateQuantity}
                        onChange={(e) => setUpdateQuantity(e.target.value)}
                        placeholder={updateType === 'ajuste' ? 'Nova quantidade' : 'Quantidade'}
                        className="flex-1 bg-zinc-600 border-zinc-500 text-white"
                      />
                      <Button
                        onClick={handleUpdateStock}
                        disabled={updating || !updateQuantity}
                        className="bg-sonangol-orange hover:bg-orange-600"
                      >
                        {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                      </Button>
                    </div>
                    <Input
                      value={updateNotes}
                      onChange={(e) => setUpdateNotes(e.target.value)}
                      placeholder="Notas (opcional)"
                      className="bg-zinc-600 border-zinc-500 text-white"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        /* Movements History */
        <div className="space-y-2">
          {movements.length === 0 ? (
            <div className="text-center py-8 text-zinc-400">
              Sem movimentos registados
            </div>
          ) : (
            movements.map((movement) => (
              <div
                key={movement.id}
                className="bg-zinc-700/50 rounded-xl p-4 border border-zinc-600"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getMovementIcon(movement.movement_type)}
                    <div>
                      <p className="text-white font-medium">{movement.product_name}</p>
                      <p className="text-zinc-400 text-xs">{movement.movement_label}</p>
                      {movement.notes && (
                        <p className="text-zinc-500 text-xs mt-1">{movement.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${movement.quantity > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {movement.previous_quantity} → {movement.new_quantity}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">{formatDate(movement.created_at)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default StockManagementPanel;
